import React from 'react';
import find from 'lodash/find';
import map from 'lodash/map';
import BreadcrumbItem from '../../containers/Breadcrumbs/BreadcrumbItem';
import routes from '../../config/routes';
import { route, routeTitles } from '../../config/constants';
import './style.css';

// Build path-pattern -> title key lookup from route constants so inner
// breadcrumb segments can resolve a title even when no route entry uses
// that exact path (e.g., /inspect is served by /inspect/:inspectionUUID?).
var routePathToTitle = (function() {
  var map = {};
  Object.keys(route).forEach(function(key) {
    if (routeTitles[key]) {
      map[route[key]] = routeTitles[key];
    }
  });
  return map;
})();

// Search a single /inspections pivot list for an inspection leaf matching uuid.
// Handles the flat { kind: 'inspection', inspectionUUID, recordsFieldTitle } shape
// and the { kind: 'assetTypeNode', inspections: [...] } group shape.
var findInspectionNameInList = function(list, uuid) {
  if (!list) return null;
  for (var i = 0; i < list.length; i++) {
    var node = list[i];
    if (node.kind === 'inspection' && node.inspectionUUID === uuid) {
      return node.recordsFieldTitle || null;
    }
    if (node.kind === 'assetTypeNode' && node.inspections) {
      for (var j = 0; j < node.inspections.length; j++) {
        var leaf = node.inspections[j];
        if (leaf.inspectionUUID === uuid) return leaf.recordsFieldTitle || null;
      }
    }
  }
  return null;
};

// Resolve the display name of an inspection by uuid. Checks the
// currentInspection, both server pivots (byInspection then byAssetType), and the
// loaded inspectionSettings (raw GET response). Used for the settings-page parent
// link, which points at a specific inspection regardless of the active selection.
var resolveInspectionName = function(inspectState, uuid) {
  if (!inspectState || !uuid) return null;
  var current = inspectState.currentInspection;
  if (current && current.uuid === uuid && current.name) return current.name;
  var byInspection = findInspectionNameInList(inspectState.inspectionsByInspection, uuid);
  if (byInspection) return byInspection;
  var byAssetType = findInspectionNameInList(inspectState.inspectionsByAssetType, uuid);
  if (byAssetType) return byAssetType;
  var raw = inspectState.inspectionSettingsRaw;
  if (raw && raw.uuid === uuid && raw.inspectionRecordsFieldTitle) {
    return raw.inspectionRecordsFieldTitle;
  }
  return null;
};

// Display an asset-type value prefixed with its field name for context, e.g.
// "Type: Foam". Falls back to the bare value when the field name is missing.
var assetTypeDisplay = function(fieldName, value) {
  var v = value !== null && value !== undefined ? value : '';
  return fieldName ? fieldName + ': ' + v : v;
};

// The asset-type field name for the active selection, looked up from the pivot
// data (selectionValue carries only hash/value or inspectionUUID/assetTypeValue).
var findAssetTypeFieldName = function(inspectState) {
  if (!inspectState) return '';
  var mode = inspectState.selectionMode;
  var value = inspectState.selectionValue;
  if (mode === 'assetType' && value && value.hash) {
    var byA = inspectState.inspectionsByAssetType || [];
    for (var i = 0; i < byA.length; i++) {
      var node = byA[i];
      if (node.kind === 'assetTypeNode' && node.assetTypeFieldPathHash === value.hash && node.assetTypeValue === value.value) {
        return node.assetTypeFieldName || '';
      }
    }
  } else if (mode === 'inspectionType' && value && value.inspectionUUID) {
    var byI = inspectState.inspectionsByInspection || [];
    for (var j = 0; j < byI.length; j++) {
      var it = byI[j];
      if (it.kind === 'inspection' && it.inspectionUUID === value.inspectionUUID) {
        return it.assetTypeFieldName || '';
      }
    }
  }
  return '';
};

// Resolve the Inspect page breadcrumb label from the active selection, matching
// the dropdown trigger text:
//   all            -> "All Inspections"
//   inspection     -> the inspection name
//   inspectionType -> the inspection name + " . " + asset-type value
//   assetType      -> the asset-type value
// Returns null when the name/value is not yet resolved so the breadcrumb can fall
// back to the generic "Inspect" label.
var resolveInspectLabel = function(inspectState) {
  if (!inspectState) return null;
  var mode = inspectState.selectionMode;
  var value = inspectState.selectionValue;
  var current = inspectState.currentInspection;
  if (mode === 'all') return 'All Inspections';
  if (mode === 'assetType') {
    if (value && value.value !== null && value.value !== undefined && value.value !== '') {
      return assetTypeDisplay(findAssetTypeFieldName(inspectState), value.value);
    }
    return null;
  }
  if (mode === 'inspection' || mode === 'inspectionType') {
    var name = current && current.name ? current.name : null;
    if (!name) return null;
    if (mode === 'inspectionType') {
      var atValue = value && value.assetTypeValue !== null && value.assetTypeValue !== undefined
        ? value.assetTypeValue
        : null;
      if (atValue !== null && atValue !== '') {
        return name + ' · ' + assetTypeDisplay(findAssetTypeFieldName(inspectState), atValue);
      }
    }
    return name;
  }
  return null;
};

/**
 * It change dynamic parts in path to new values if possible
 */
var handleDynamicPathParts = function(path, objectWithDynamicPartsValues) {
  var parts = path.split('/');
  return map(parts, function(part) {
    if (part.includes(':')) {
      var newPart = part.replace(':', '').replace('?', '');
      return objectWithDynamicPartsValues[newPart] || newPart;
    }
    return part;
  }).join('/');
};

var Breadcrumbs = function(props) {
  var pathname = props.path;
  if (pathname.slice(-1) === '/') {
    pathname = pathname.slice(0, -1);
  }
  var currentRoute = find(routes, { path: pathname });
  var currentRouteTitle = currentRoute ? currentRoute.title : '';

  if (currentRoute && currentRoute.title === 'dynamicContent') {
    // Merge user and account modules (user takes precedence for same key)
    var userModules = props.currentUser && props.currentUser.customModules ? props.currentUser.customModules : [];
    var accountModules = props.currentAccount && props.currentAccount.customModules ? props.currentAccount.customModules : [];
    var moduleMap = {};
    accountModules.forEach(function(module) {
      if (module.key) {
        moduleMap[module.key] = module;
      }
    });
    userModules.forEach(function(module) {
      if (module.key) {
        moduleMap[module.key] = module;
      }
    });
    var mergedModules = Object.keys(moduleMap).map(function(key) {
      return moduleMap[key];
    });

    // Find module by slug from URL params
    var slug = props.params && props.params.slug;
    var currentModule = null;
    for (var i = 0; i < mergedModules.length; i++) {
      if (mergedModules[i].slug === slug) {
        currentModule = mergedModules[i];
        break;
      }
    }
    currentRouteTitle = currentModule ? currentModule.tabName : '';
  }

  var hasInnerRoutes = currentRoute && currentRoute.innerRoutePaths && currentRoute.innerRoutePaths.length > 0;

  // Resolve the inspection name for breadcrumb segments that point at the
  // inspection page (mirrors how slate-view breadcrumbs show the view name).
  var inspectionName = resolveInspectionName(
    props.inspect,
    props.params && props.params.inspectionUUID
  );

  // On the Inspect page, the last (current-page) breadcrumb reflects the active
  // selection (matching the dropdown trigger) rather than the generic "Inspect".
  // All Inspect selection routes (INSPECT_SELECTION / INSPECT_ALL /
  // INSPECT_ASSET_TYPE / INSPECT_SELECTION_TYPE) share the routeTitles.INSPECT_SELECTION title.
  var inspectSelectionLabel = resolveInspectLabel(props.inspect);
  var isInspectSelectionPage = currentRouteTitle === routeTitles.INSPECT_SELECTION;
  var currentNameOverride = isInspectSelectionPage && inspectSelectionLabel ? inspectSelectionLabel : null;

  // For slate-view breadcrumbs (title === SLATE_VIEWS), the name is pulled
  // from state.slateViews.currentSlateView. On refresh that loads async, so
  // the breadcrumb briefly shows the generic "View" label before the name
  // arrives. Detect the pending-load state so we can skip rendering until
  // the matching view is available.
  var viewsParam = props.params && props.params.views;
  var isViewPending =
    !!viewsParam
    && !props.newSlateViewType
    && (!props.currentSlateView || props.currentSlateView.uuid !== viewsParam);

  return (
    <div className="breadcrumbs">
      {/* Parent routes from innerRoutePaths - these are links */}
      {hasInnerRoutes &&
        map(currentRoute.innerRoutePaths, function(innerRoutePath) {
          var innerRoute = find(routes, { path: innerRoutePath });
          var innerTitle = innerRoute
            ? innerRoute.title
            : routePathToTitle[innerRoutePath] || '';
          var updatedInnerRoutePath = handleDynamicPathParts(
            innerRoutePath,
            props.params
          );
          var isInspectionSegment = innerTitle === routeTitles.INSPECT_SELECTION;
          var isViewSegment = innerTitle === routeTitles.SLATE_VIEWS;
          // Avoid flashing the generic "Inspect"/"View" label on refresh before
          // the resource name resolves: if this segment targets a specific
          // inspection/view but the name hasn't loaded yet, skip rendering
          // until it does.
          if (
            isInspectionSegment
            && props.params
            && props.params.inspectionUUID
            && !inspectionName
          ) {
            return null;
          }
          if (isViewSegment && isViewPending) {
            return null;
          }
          var nameOverride = isInspectionSegment ? inspectionName : null;
          return (
            <BreadcrumbItem
              path={updatedInnerRoutePath}
              title={innerTitle}
              nameOverride={nameOverride}
              isLink={true}
              key={'breadcrumb-key-' + updatedInnerRoutePath}
            />
          );
        })}

      {/* Current page - not a link. Skip while a view-name-dependent
          breadcrumb is still loading to avoid flashing "View". */}
      {!(currentRouteTitle === routeTitles.SLATE_VIEWS && isViewPending) && (
        <BreadcrumbItem
          path={pathname}
          title={currentRouteTitle}
          nameOverride={currentNameOverride}
          isLink={false}
          key={pathname}
        />
      )}
    </div>
  );
};

export default Breadcrumbs;
